// AiUsageCallDrawer — pen H-07 (+ the H-10 cases): one call's ledger entry
// in a drawer over the call list. Who and where, model and prompt, tokens
// and cost per class with the rate-table version, timing, and (beyond the
// frame, design gap) the call type, what it used (entities, files, tools)
// and its retries. The raw record is not kept yet (#87): the panel says so
// honestly instead of offering buttons (H-08's honest state).
//
// Pure projection of the detail read. Closing, filtering by an id and
// opening the rate table are route changes; copying is `onCopy`.

export { AiUsageCallDrawer };

import type {
    AiUsageCallDetail,
    AiUsageCost,
    AiUsageEntity,
} from "@bilda/app/agentCallAdmin";
import { Remote } from "@bilda/utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import {
    formatInt,
    formatMoney,
    formatSeconds,
    siteDetailLabel,
} from "../../app/aiUsage/format.js";
import { type AiUsageRoute, withFilter } from "../../app/aiUsage/route.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import type { AiUsageRead } from "./AiUsagePage.js";
import { LoadFailed, OutcomeBadge } from "./AiUsageStates.js";
// The shared frame styles first, so this view's rules win ties.
import "./AiUsage.css";
import "./AiUsageCallDrawer.css";

type Props = {
    readonly route: AiUsageRoute;
    readonly detail: AiUsageRead<AiUsageCallDetail>;
    readonly onNavigate: (route: AiUsageRoute) => void;
    readonly onCopy: (text: string) => void;
    readonly onRetry: () => void;
};

const Section = ({
    title,
    children,
}: {
    readonly title: string;
    readonly children: ReactNode;
}) => (
    <section className="ai-usage__section">
        <h3 className="ai-usage__section-title">{title}</h3>
        {children}
    </section>
);

const Field = ({
    label,
    children,
}: {
    readonly label: string;
    readonly children: ReactNode;
}) => (
    <div className="ai-usage__field">
        <dt>{label}</dt>
        <dd>{children}</dd>
    </div>
);

const Copyable = ({
    value,
    onCopy,
}: {
    readonly value: string;
    readonly onCopy: (text: string) => void;
}) => {
    const { t } = useLingui();
    return (
        <span className="ai-usage__copyable">
            <code className="ai-usage__id">{value}</code>
            <IconButton
                size="sm"
                variant="ghost"
                label={t`Copy ${value}`}
                onClick={() => onCopy(value)}
            >
                <Icon name="copy" size="xs" />
            </IconButton>
        </span>
    );
};

const StopReason = ({ d }: { readonly d: AiUsageCallDetail }) => {
    const { t } = useLingui();
    const o = d.call.outcome;
    switch (o.type) {
        case "call_completed":
            if (o.stop.type === "stop_unknown")
                return (
                    <>
                        {t`Unknown`} · {t`sent as`} <code>{o.stop.raw}</code>
                    </>
                );
            return <code>{o.stop.raw}</code>;
        case "call_partial":
            return t`None – the stream ended early`;
        case "call_failed":
            return o.httpStatus === undefined
                ? t`None – no response`
                : t`None – HTTP ${o.httpStatus}`;
        case "call_cancelled":
            return t`None – cancelled`;
        case "outcome_unknown":
            return (
                <>
                    {t`Unknown`} · {t`sent as`} <code>{o.raw}</code>
                </>
            );
    }
};

const CostTable = ({
    d,
    onRates,
}: {
    readonly d: AiUsageCallDetail;
    readonly onRates: () => void;
}) => {
    const { t, i18n } = useLingui();
    const loc = i18n.locale;
    const c = d.call;
    const cost: AiUsageCost = d.cost;
    const input = c.inputTokens + c.cacheReadTokens + c.cacheWriteTokens;
    const priced = cost.type === "cost_priced" ? cost : undefined;
    const money = (micro: number | undefined) =>
        micro === undefined ? "—" : formatMoney(loc, micro, true);
    if (cost.type === "cost_no_usage")
        return (
            <p className="ai-usage__muted">
                {t`No tokens were reported: the call ended before the vendor counted any. Cost unknown.`}
            </p>
        );
    const share = input === 0 ? 0 : c.cacheReadTokens / input;
    return (
        <>
            <table className="ai-usage__cost">
                <tbody>
                    <tr>
                        <th scope="row">{t`Input`}</th>
                        <td>{formatInt(loc, c.inputTokens)}</td>
                        <td className="ai-usage__num">
                            {money(priced?.inputMicroUsd)}
                        </td>
                    </tr>
                    <tr>
                        <th scope="row">{t`Output`}</th>
                        <td>
                            {c.outputTokens === 0
                                ? t`no output`
                                : formatInt(loc, c.outputTokens)}
                        </td>
                        <td className="ai-usage__num">
                            {money(priced?.outputMicroUsd)}
                        </td>
                    </tr>
                    <tr>
                        <th scope="row">{t`Cache read`}</th>
                        <td>
                            {formatInt(loc, c.cacheReadTokens)}
                            {c.cacheReadTokens > 0
                                ? ` ${t`(${Math.round(share * 100)}% of input)`}`
                                : ""}
                        </td>
                        <td className="ai-usage__num">
                            {money(priced?.cacheReadMicroUsd)}
                        </td>
                    </tr>
                    <tr>
                        <th scope="row">{t`Cache write`}</th>
                        <td>{formatInt(loc, c.cacheWriteTokens)}</td>
                        <td className="ai-usage__num">
                            {money(priced?.cacheWriteMicroUsd)}
                        </td>
                    </tr>
                    <tr className="ai-usage__cost-total">
                        <th scope="row">{t`Total`}</th>
                        <td>
                            {c.usageType === "usage_partial"
                                ? t`at least, the stream broke`
                                : ""}
                        </td>
                        <td className="ai-usage__num">
                            {priced === undefined
                                ? t`unknown`
                                : money(priced.totalMicroUsd)}
                        </td>
                    </tr>
                </tbody>
            </table>
            {priced === undefined ? (
                <p className="ai-usage__warn">
                    {t`No price: ${c.modelId} is not in the rate table. The cost stays unknown until a rate is added.`}
                </p>
            ) : (
                <button
                    type="button"
                    className="ai-usage__link"
                    onClick={onRates}
                >
                    {t`Priced with rate table v${priced.rateVersion}, valid from ${new Intl.DateTimeFormat(loc, { day: "numeric", month: "short", year: "numeric" }).format(new Date(priced.rateValidFrom))}`}
                    <Icon name="arrow-right" size="xs" />
                </button>
            )}
        </>
    );
};

const Timing = ({ d }: { readonly d: AiUsageCallDetail }) => {
    const { t, i18n } = useLingui();
    const loc = i18n.locale;
    const total = Math.max(1, d.call.durationMs);
    const ttft = d.call.ttftMs;
    return (
        <>
            <div className="ai-usage__timing" aria-hidden="true">
                <span
                    className="ai-usage__timing-wait"
                    style={{
                        width: `${ttft === null ? 100 : (ttft / total) * 100}%`,
                    }}
                />
                {ttft === null ? null : (
                    <span className="ai-usage__timing-mark" />
                )}
            </div>
            <p className="ai-usage__timing-text">
                {ttft === null
                    ? t`No first token · stopped after ${formatSeconds(loc, d.call.durationMs)}`
                    : t`First token ${formatSeconds(loc, ttft)} · Total ${formatSeconds(loc, d.call.durationMs)}`}
            </p>
        </>
    );
};

// What a call did with an entity (subject, context, input, output).
const roleLabel = (role: string): MessageDescriptor => {
    switch (role) {
        case "subject":
            return msg({ message: "About", context: "agent call role" });
        case "context":
            return msg({ message: "Context", context: "agent call role" });
        case "input":
            return msg({ message: "Read", context: "agent call role" });
        case "output":
            return msg({ message: "Wrote", context: "agent call role" });
        default:
            return msg`Other`;
    }
};

const kindLabel = (type: string): MessageDescriptor => {
    switch (type) {
        case "conversation":
            return msg`Conversation`;
        case "resource":
            return msg`Resource`;
        case "learning_context":
            return msg`Learning context`;
        case "claim":
            return msg`Claim`;
        case "annotation":
            return msg`Annotation`;
        default:
            return msg`Other`;
    }
};

const EntityLine = ({
    e,
    onFilter,
}: {
    readonly e: AiUsageEntity;
    readonly onFilter: (id: string) => void;
}) => {
    const { t, i18n } = useLingui();
    return (
        <li className="ai-usage__ref">
            <span className="ai-usage__ref-kind">
                {i18n._(kindLabel(e.type))}
            </span>
            <span className="ai-usage__ref-title" title={e.title ?? e.id}>
                {e.title ?? t`Title not found`}
            </span>
            <button
                type="button"
                className="ai-usage__ref-id"
                title={t`Show every call that used ${e.id}`}
                onClick={() => onFilter(e.id)}
            >
                <code>{e.id.slice(0, 8)}</code>
            </button>
        </li>
    );
};

const References = ({
    d,
    route,
    onNavigate,
}: {
    readonly d: AiUsageCallDetail;
    readonly route: AiUsageRoute;
    readonly onNavigate: (route: AiUsageRoute) => void;
}) => {
    const { t, i18n } = useLingui();
    const loc = i18n.locale;
    const byEntity = (id: string) =>
        onNavigate(withFilter(route, "entity", id));
    const roles = ["subject", "context", "input", "output"];
    const grouped = [
        ...roles.map(
            (r) => [r, d.entities.filter((e) => e.role === r)] as const,
        ),
        ["other", d.entities.filter((e) => !roles.includes(e.role))] as const,
    ].filter(([, es]) => es.length > 0);
    const nothing =
        grouped.length === 0 &&
        d.files.length === 0 &&
        d.toolCalls.length === 0;
    return (
        <Section title={t`What it used`}>
            {nothing ? (
                <p className="ai-usage__muted">{t`No ids, files or tools recorded for this call.`}</p>
            ) : null}
            {grouped.map(([role, es]) => (
                <div key={role} className="ai-usage__ref-group">
                    <span className="ai-usage__ref-role">
                        {i18n._(roleLabel(role))}
                    </span>
                    <ul className="ai-usage__refs">
                        {es.map((e) => (
                            <EntityLine
                                key={`${e.type}:${e.id}`}
                                e={e}
                                onFilter={byEntity}
                            />
                        ))}
                    </ul>
                </div>
            ))}
            {d.files.length > 0 ? (
                <div className="ai-usage__ref-group">
                    <span className="ai-usage__ref-role">{t`Files`}</span>
                    <ul className="ai-usage__refs">
                        {d.files.map((f) => (
                            <li key={f.contentHash} className="ai-usage__ref">
                                <span
                                    className="ai-usage__ref-title"
                                    title={f.title ?? f.contentHash}
                                >
                                    {f.title ?? t`Unnamed file`}
                                    {f.pages.length > 0
                                        ? ` · ${t`pages`} ${f.pages.map((p) => (p.first === p.last ? `${p.first}` : `${p.first}–${p.last}`)).join(", ")}`
                                        : ""}
                                </span>
                                <button
                                    type="button"
                                    className="ai-usage__ref-id"
                                    title={t`Show every call that read this file`}
                                    onClick={() =>
                                        onNavigate(
                                            withFilter(
                                                route,
                                                "file",
                                                f.contentHash,
                                            ),
                                        )
                                    }
                                >
                                    <code>{f.contentHash.slice(0, 8)}</code>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
            {d.toolCalls.length > 0 ? (
                <div className="ai-usage__ref-group">
                    <span className="ai-usage__ref-role">{t`Tools run`}</span>
                    <ul className="ai-usage__refs">
                        {d.toolCalls.map((tc) => (
                            <li
                                key={tc.toolCallId}
                                className="ai-usage__ref ai-usage__ref--tool"
                            >
                                <button
                                    type="button"
                                    className="ai-usage__ref-id"
                                    title={t`Show every call that ran ${tc.name}`}
                                    onClick={() =>
                                        onNavigate(
                                            withFilter(route, "tool", tc.name),
                                        )
                                    }
                                >
                                    <code>{tc.name}</code>
                                </button>
                                <span className="ai-usage__muted">
                                    {formatSeconds(loc, tc.durationMs)}
                                    {tc.outcomeType === "tool_failed"
                                        ? ` · ${t`failed`}${tc.failureCategory === null ? "" : ` (${tc.failureCategory})`}`
                                        : ""}
                                </span>
                                {tc.targets.length > 0 ? (
                                    <span className="ai-usage__ref-title">
                                        {tc.targets
                                            .map(
                                                (x) =>
                                                    x.title ?? x.id.slice(0, 8),
                                            )
                                            .join(", ")}
                                    </span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
            {d.toolsOffered.length > 0 ? (
                <details className="ai-usage__offered">
                    <summary>{t`${formatInt(loc, d.toolsOffered.length)} tools offered`}</summary>
                    <p className="ai-usage__mono-list">
                        {d.toolsOffered.join(", ")}
                    </p>
                </details>
            ) : null}
        </Section>
    );
};

const Body = ({
    d,
    route,
    onNavigate,
    onCopy,
}: {
    readonly d: AiUsageCallDetail;
    readonly route: AiUsageRoute;
    readonly onNavigate: (route: AiUsageRoute) => void;
    readonly onCopy: (text: string) => void;
}) => {
    const { t, i18n } = useLingui();
    const c = d.call;
    const user =
        c.user?.label ??
        c.user?.id ??
        (c.initiatorType === "system"
            ? t`Background work (no user)`
            : t`Unknown`);
    return (
        <div className="ai-usage__drawer-body">
            <Section title={t`Who and where`}>
                <dl className="ai-usage__fields">
                    <Field label={t`User`}>{user}</Field>
                    <Field label={t`Workspace`}>
                        {c.workspace === null
                            ? t`None – shared work (a file read once for everyone)`
                            : (c.workspace.label ?? t`Untitled workspace`)}
                    </Field>
                    {d.conversation === null ? null : (
                        <Field label={t`Conversation`}>
                            <span className="ai-usage__ref-title">
                                {d.conversation.title ?? t`Title not found`}
                                {c.site.type === "chat_round"
                                    ? ` · ${t`round ${c.site.round + 1}`}`
                                    : ""}
                            </span>
                        </Field>
                    )}
                </dl>
            </Section>
            <Section title={t`Call`}>
                <dl className="ai-usage__fields">
                    <Field label={t`Type`}>
                        {i18n._(siteDetailLabel(c.site))}
                    </Field>
                    <Field label={t`Call ID`}>
                        <Copyable value={c.callId} onCopy={onCopy} />
                    </Field>
                    <Field label={t`Attempt`}>
                        {d.attempts.length > 1
                            ? t`${c.attempt} of ${d.attempts.length} for this round`
                            : formatInt(i18n.locale, c.attempt)}
                    </Field>
                    {d.parentToolCallId === null ? null : (
                        <Field label={t`Inside tool call`}>
                            <code className="ai-usage__id">
                                {d.parentToolCallId}
                            </code>
                        </Field>
                    )}
                </dl>
                {d.attempts.length > 1 ? (
                    <ul className="ai-usage__attempts">
                        {d.attempts.map((a) => (
                            <li key={a.callId}>
                                <button
                                    type="button"
                                    className="ai-usage__ref-id"
                                    aria-current={
                                        a.callId === c.callId
                                            ? "true"
                                            : undefined
                                    }
                                    onClick={() =>
                                        onNavigate({
                                            ...route,
                                            callId: a.callId,
                                        })
                                    }
                                >
                                    {t`Try ${a.attempt}`}
                                </button>
                                <OutcomeBadge
                                    outcome={a.outcome}
                                    toolCalls={0}
                                />
                            </li>
                        ))}
                    </ul>
                ) : null}
            </Section>
            <Section title={t`Model and prompt`}>
                <dl className="ai-usage__fields">
                    <Field label={t`Model`}>
                        <code>{c.modelId}</code>
                    </Field>
                    <Field label={t`Prompt version`}>
                        <code
                            title={t`Fingerprint of the system prompt and tool definitions`}
                        >
                            {d.promptVersion}
                        </code>
                    </Field>
                    <Field label={t`Stop reason`}>
                        <StopReason d={d} />
                    </Field>
                    <Field label={t`Tool calls`}>
                        {d.toolCalls.length === 0
                            ? "0"
                            : `${d.toolCalls.length} · ${d.toolCalls.map((x) => x.name).join(", ")}`}
                    </Field>
                </dl>
                {d.call.outcome.type === "call_completed" &&
                d.call.outcome.stop.type === "stop_unknown" ? (
                    <p className="ai-usage__info">
                        <Icon name="info" size="sm" />
                        {t`Not mapped to a known reason yet. It still counts in totals.`}
                    </p>
                ) : null}
            </Section>
            <Section title={t`Tokens and cost`}>
                <CostTable
                    d={d}
                    onRates={() => {
                        const { callId: _closed, ...rest } = route;
                        onNavigate({ ...rest, tab: "rates" });
                    }}
                />
            </Section>
            <Section title={t`Timing`}>
                <Timing d={d} />
            </Section>
            <References d={d} route={route} onNavigate={onNavigate} />
            <Section title={t`Raw record`}>
                <div className="ai-usage__raw">
                    <span className="ai-usage__raw-title">
                        <Icon name="shield" size="sm" />
                        {t`Not saved`}
                    </span>
                    <p>
                        {t`The exact request and response are not stored yet. The ledger above is complete.`}
                    </p>
                </div>
            </Section>
        </div>
    );
};

const AiUsageCallDrawer = ({
    route,
    detail,
    onNavigate,
    onCopy,
    onRetry,
}: Props) => {
    const { t, i18n } = useLingui();
    const close = () => {
        const { callId: _closed, ...rest } = route;
        onNavigate(rest);
    };
    const d = Remote.match<
        AiUsageCallDetail,
        unknown,
        AiUsageCallDetail | undefined
    >({
        notAsked: () => undefined,
        loading: () => undefined,
        reloading: (prev) => prev,
        loaded: (v) => v,
        failed: () => undefined,
    })(detail);
    const when =
        d === undefined
            ? ""
            : new Intl.DateTimeFormat(i18n.locale, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hourCycle: "h23",
              }).format(new Date(d.call.startedAt));
    return (
        <DialogPrimitive.Root
            open
            onOpenChange={(open) => {
                if (!open) close();
            }}
        >
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="ai-usage__scrim" />
                <DialogPrimitive.Content
                    className="ai-usage__drawer"
                    // Focus the drawer itself, not the close button: the
                    // reader starts at the top, Tab reaches every control.
                    onOpenAutoFocus={(e) => {
                        e.preventDefault();
                        if (e.currentTarget instanceof HTMLElement)
                            e.currentTarget.focus();
                    }}
                >
                    <header className="ai-usage__drawer-head">
                        <DialogPrimitive.Title className="ai-usage__drawer-title">
                            {d === undefined ? t`Call` : t`Call · ${when}`}
                        </DialogPrimitive.Title>
                        <DialogPrimitive.Close asChild>
                            <IconButton label={t`Close`} variant="ghost">
                                <Icon name="close" size="sm" />
                            </IconButton>
                        </DialogPrimitive.Close>
                        {d === undefined ? null : (
                            <div className="ai-usage__drawer-meta">
                                <OutcomeBadge
                                    outcome={d.call.outcome}
                                    toolCalls={d.call.toolCalls}
                                    pill
                                />
                                <span className="ai-usage__muted">{t`Request ID`}</span>
                                {d.call.vendorRequestId === null ? (
                                    <span className="ai-usage__muted">{t`none – no response`}</span>
                                ) : (
                                    <Copyable
                                        value={d.call.vendorRequestId}
                                        onCopy={onCopy}
                                    />
                                )}
                            </div>
                        )}
                    </header>
                    <DialogPrimitive.Description className="ai-usage__visually-hidden">
                        {t`Everything the ledger recorded about this call.`}
                    </DialogPrimitive.Description>
                    {detail._tag === "Remote.Failed" ? (
                        <LoadFailed onRetry={onRetry} />
                    ) : d === undefined ? (
                        <div className="ai-usage__drawer-body" aria-busy="true">
                            {[0, 1, 2, 3].map((i) => (
                                <Skeleton
                                    key={i}
                                    variant="block"
                                    height="var(--space-16)"
                                />
                            ))}
                        </div>
                    ) : (
                        <Body
                            d={d}
                            route={route}
                            onNavigate={onNavigate}
                            onCopy={onCopy}
                        />
                    )}
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};
